import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { AxiosPublico } from "../../../componentes/axios/Axios";
import Card from '../../../componentes/contenedores/Card';
import GraficoBarra from '../../../componentes/graficos/Barras';
import FiltroFechaUI from "../../../componentes/metodos/FiltroFechaUI";
import useFechaFiltro from "../../../componentes/metodos/LogicaFiltro";
import Page from '../../../componentes/plantilla/Page';
import {
    AnalisisTopClientes,
    AnalisisTopFrecuenciaClientes,
    AnalisisResumenKPICliente,
    AnalisisMoraCliente
} from '../../../configuracion/apiUrls';
import { formatoMoneda } from "../../../configuracion/validaciones";
moment.locale("es");

const ClientesAnalisis = () => {
    const [cargando, setCargando] = useState(false);
    const [chartTopClientes, setChartTopClientes] = useState(null);
    const [chartFrecuenciaClientes, setChartFrecuenciaClientes] = useState(null);
    const [chartMora, setChartMora] = useState(null);
    const [metricas, setMetricas] = useState(null);
    const [tablaTopInversion, setTablaTopInversion] = useState([]);
    const [tablaTopFrecuencia, setTablaTopFrecuencia] = useState([]);

    const {
        filtro, setFiltro, fechaSeleccionada, setFechaSeleccionada,
        horaSeleccionada, setHoraSeleccionada, semanaSeleccionada, setSemanaSeleccionada,
        mesSeleccionado, setMesSeleccionado, anioSeleccionado, setAnioSeleccionado,
        obtenerFechasDeFiltro, maxAnio,
    } = useFechaFiltro();

    const cargarTodo = useCallback(async () => {
        setCargando(true);
        const fechas = obtenerFechasDeFiltro();
        const params = {
            fechaInicio: moment(fechas.fechaInicio).format('YYYY-MM-DD HH:mm:ss'),
            fechaFin: moment(fechas.fechaFin).format('YYYY-MM-DD HH:mm:ss'),
        };

        try {
            const [resClientes, resFrecuencia, resResumen, resMora] = await Promise.all([
                AxiosPublico.get(AnalisisTopClientes, { params }),
                AxiosPublico.get(AnalisisTopFrecuenciaClientes, { params }),
                AxiosPublico.get(AnalisisResumenKPICliente, { params }),
                AxiosPublico.get(AnalisisMoraCliente, { params }),
            ]);

            // 1. Top Clientes por Inversión (Basado en sum('totalGastado') del controlador)
            if (resClientes.data) {
                setChartTopClientes({
                    labels: resClientes.data.map(c => c.Cliente?.nombreCompleto || 'Desconocido'),
                    data: resClientes.data.map(c => parseFloat(c.totalGastado)),
                    colors: resClientes.data.map(() => `rgba(3, 19, 167, 0.89)`),
                    titulo: "Top 10 Inversión"
                });
                setTablaTopInversion(resClientes.data);
            }

            // 2. Frecuencia de Compra (Basado en count('id') del controlador)
            if (resFrecuencia.data) {
                setChartFrecuenciaClientes({
                    labels: resFrecuencia.data.map(c => c.Cliente?.nombreCompleto || 'Desconocido'),
                    data: resFrecuencia.data.map(c => parseInt(c.frecuenciaCompra)),
                    colors: resFrecuencia.data.map(() => `rgba(255, 99, 132, 0.8)`),
                    titulo: "Frecuencia de Compra"
                });
                setTablaTopFrecuencia(resFrecuencia.data);
            }

            // 3. Resumen KPIs (Métricas Globales)
            setMetricas(resResumen.data);

            // 4. Análisis de Mora (Rangos: 1-30, 31-60, 61-90)
            if (resMora.data) {
                setChartMora({
                    labels: resMora.data.map(m => m.rango),
                    data: resMora.data.map(m => parseFloat(m.montoVencido)),
                    colors: ['#f6c23e', '#fd7e14', '#e74a3b', '#858796'],
                    titulo: "Mora por Antigüedad"
                });
            }

        } catch (error) {
            console.error("Error al sincronizar con el controlador:", error);
        } finally {
            setCargando(false);
        }
    }, [obtenerFechasDeFiltro]);

    useEffect(() => { cargarTodo(); }, []);

    const datos = {
        titulo: {
            titulo: "Página principal",
            url: "/app/admin/analisis/clientes",
            tituloUrl: "Menu Principal",
            nombreUrl: "Inicio ",
        },
    };
    return (
        <Page datos={datos}>
            <Card>
                <FiltroFechaUI
                    filtro={filtro} setFiltro={setFiltro}
                    fechaSeleccionada={fechaSeleccionada} setFechaSeleccionada={setFechaSeleccionada}
                    horaSeleccionada={horaSeleccionada} setHoraSeleccionada={setHoraSeleccionada}
                    semanaSeleccionada={semanaSeleccionada} setSemanaSeleccionada={setSemanaSeleccionada}
                    mesSeleccionado={mesSeleccionado} setMesSeleccionado={setMesSeleccionado}
                    anioSeleccionado={anioSeleccionado} setAnioSeleccionado={setAnioSeleccionado}
                    handleApplyFilter={cargarTodo}
                    maxAnio={maxAnio}
                />
                <hr />

                {cargando ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status"></div>
                    </div>
                ) : (
                    <>
                            <div className="row mb-4">
                                {/* Clientes Activos */}
                                <div className="col-lg-3 col-md-6 mb-3">
                                    <div className="p-3 border-start border-primary border-4 bg-light rounded shadow-sm h-100">
                                        <h6 className="text-muted small text-uppercase fw-bold">Clientes activos</h6>
                                        <h3 className="fw-bold mb-0">{metricas?.totalClientes || 0}</h3>
                                    </div>
                                </div>

                                {/* Promedio Compra */}
                                <div className="col-lg-3 col-md-6 mb-3">
                                    <div className="p-3 border-start border-success border-4 bg-light rounded shadow-sm h-100">
                                        <h6 className="text-muted small text-uppercase fw-bold">Promedio compra</h6>
                                        <h3 className="fw-bold mb-0">
                                            {formatoMoneda(parseFloat(metricas?.promedioCompra || 0))}
                                        </h3>
                                    </div>
                                </div>

                                {/* Crédito Total */}
                                <div className="col-lg-3 col-md-6 mb-3">
                                    <div className="p-3 border-start border-info border-4 bg-light rounded shadow-sm h-100">
                                        <h6 className="text-muted small text-uppercase fw-bold">Crédito Total</h6>
                                        <h3 className="fw-bold mb-0">
                                            {formatoMoneda(parseFloat(metricas?.totalCredito || 0))}
                                        </h3>
                                    </div>
                                </div>

                                {/* Tasa de Mora */}
                                <div className="col-lg-3 col-md-6 mb-3">
                                    <div className="p-3 border-start border-danger border-4 bg-light rounded shadow-sm h-100">
                                        <h6 className="text-muted small text-uppercase fw-bold">Tasa de Mora</h6>
                                        <h3 className="fw-bold mb-0 text-danger">{metricas?.tasaMora || 0}%</h3>
                                    </div>
                                </div>
                            </div>
                        {/* GRÁFICOS */}
                        <div className="row mb-5">
                            <div className="col-lg-4">
                                {chartTopClientes && <GraficoBarra datos={chartTopClientes} esMonetario={true} />}
                            </div>
                            <div className="col-lg-4">
                                {chartFrecuenciaClientes && <GraficoBarra datos={chartFrecuenciaClientes} esMonetario={false} />}
                            </div>
                            <div className="col-lg-4">
                                {chartMora && <GraficoBarra datos={chartMora} esMonetario={true} />}
                            </div>
                        </div>

                        {/* TABLAS TOP 10 */}
                        <div className="row">
                            <div className="col-md-6">
                                <h6 className="fw-bold"><i className="fas fa-trophy text-warning"></i> Top 10 por Valor de Compra</h6>
                                <table className="table table-sm table-striped">
                                    <thead>
                                        <tr>
                                            <th>Nombre del cliente</th>
                                            <th>Total comprado</th>
                                            <th># Compras</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tablaTopInversion.map((c, i) => (
                                            <tr key={i}>
                                                <td>{c.Cliente?.nombreCompleto}</td>
                                                <td className="fw-bold">{formatoMoneda(parseFloat(c.totalGastado || 0))}</td>
                                                <td>{c.frecuenciaCompra || 0}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="col-md-6">
                                <h6 className="fw-bold"><i className="fas fa-sync text-primary"></i> Top 10 por Frecuencia</h6>
                                <table className="table table-sm table-striped">
                                    <thead>
                                        <tr>
                                            <th>Nombre del cliente</th>
                                            <th>Número de compras</th>
                                            <th>Total comprado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tablaTopFrecuencia.map((c, i) => (
                                            <tr key={i}>
                                                <td>{c.Cliente?.nombreCompleto}</td>
                                                <td className="fw-bold text-center">{c.frecuenciaCompra}</td>
                                                <td>{formatoMoneda(parseFloat(c.totalGastado || 0))}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </Card>
        </Page>
    );
}

export default ClientesAnalisis;